import React from "react";
import moment from "moment";
import styled from "styled-components";
import { queryCache } from "react-query";
import { useRouter } from "next/router";
import { Button, Alert, Row, Col, DatePicker } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import { apiAxios } from "services/HttpServices";
import { joiResolver } from "@hookform/resolvers";
import Joi from "@hapi/joi";

import { TextInputSkeleton } from "lib/loader/shared/textInputSkeleton";
import { TInput } from "components/form/Field/TInput";
import { clean } from "lib/utils";

const datelines = Joi.object({
  title: Joi.string().required(),
}).unknown();

const templateSchema = Joi.object({
  template: Joi.array().items(datelines),
});

const formatTemplate = (template) => {
  const newDatelines = template.map((dateline) => {
    const formattedDatelines = {
      ...dateline,
      due_at: dateline.due_after
        ? moment().add(dateline.due_after, "days").utc().format()
        : null,
    };
    return formattedDatelines;
  });
  return newDatelines;
};

const ManageDatelineForm = (props) => {
  const [error, setError] = React.useState("");
  const router = useRouter();
  const { id } = router.query;
  const {
    state: notificationState,
    setSuccessMessage,
    clearSuccessMessage,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);
  const { handleSubmit, errors, control, reset, setValue } = useForm({
    defaultValues: {
      template: props?.template
        ? formatTemplate(props.template)
        : [
            {
              title: undefined,
              due_at: undefined,
            },
          ],
    },
    resolver: joiResolver(templateSchema),
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "template",
  });

  const onSubmit = async (data) => {
    setError("");
    setLoading("createDateline");
    const formattedTemplates = data?.template.map((dateline) => {
      const newFormattedTemplate = {
        ...dateline,
        due_at: dateline.due_at ? dateline.due_at.format() : null,
      };

      clean(newFormattedTemplate);
      return newFormattedTemplate;
    });

    try {
      const res = await apiAxios.post(
        `/jobs/${id}/dateline`,
        formattedTemplates
      );

      queryCache.setQueryData(["job-detail", id.toString()], function (
        oldData
      ) {
        const newData = {
          ...oldData,
          dateline: res.data.data,
        };
        return newData;
      });
      props.setDoneCreate(true);
      finishLoading("createDateline");
      setSuccessMessage("Succesfully created dateline");
      clearSuccessMessage();
    } catch (e) {
      finishLoading("createDateline");
      setError(e.response.data.message);
    }
  };

  React.useEffect(() => {
    reset({
      template: props.template
        ? formatTemplate(props.template)
        : [
            {
              title: undefined,
              due_at: undefined,
            },
          ],
    });
  }, [props.template]);

  return (
    <>
      {error && (
        <div className="alert">
          <Alert message={error} type="error" showIcon />
        </div>
      )}
      <>
        {props.status === "loading" ? (
          <ItemContainer>
            <Row gutter={4} align="middle">
              <Col xs={18}>
                <TextInputSkeleton width="70%" />
              </Col>
              <Col xs={5}>
                <TextInputSkeleton width="70%" />
              </Col>

              <Col xs={1} align="middle">
                <TextInputSkeleton width="70%" />
              </Col>
            </Row>
            <Row gutter={4} align="middle">
              <Col xs={18}>
                <TextInputSkeleton width="70%" />
              </Col>
              <Col xs={5}>
                <TextInputSkeleton width="70%" />
              </Col>

              <Col xs={1} align="middle">
                <TextInputSkeleton width="70%" />
              </Col>
            </Row>
            <Row gutter={4} align="middle">
              <Col xs={18}>
                <TextInputSkeleton width="70%" />
              </Col>
              <Col xs={5}>
                <TextInputSkeleton width="70%" />
              </Col>

              <Col xs={1} align="middle">
                <TextInputSkeleton width="70%" />
              </Col>
            </Row>
            <Row gutter={4} align="middle">
              <Col xs={18}>
                <TextInputSkeleton width="70%" />
              </Col>
              <Col xs={5}>
                <TextInputSkeleton width="70%" />
              </Col>

              <Col xs={1} align="middle">
                <TextInputSkeleton width="70%" />
              </Col>
            </Row>
          </ItemContainer>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            {fields.length > 0 && (
              <>
                {fields.map((item, index) => {
                  return (
                    <ItemContainer key={item.id}>
                      <Row gutter={4} align="middle">
                        <Col xs={18}>
                          <TInput
                            errors={errors}
                            name={`template[${index}].title`}
                            placeholder="Dateline title"
                            defaultValue={item.title}
                            propsInput={{
                              control: control,
                            }}
                          />
                        </Col>

                        <Col xs={5}>
                          <Controller
                            control={control}
                            name={`template[${index}].due_at`}
                            defaultValue={
                              item.due_at ? moment(item.due_at) : null
                            }
                            render={() => (
                              <DatePicker
                                onChange={(e) => {
                                  setValue(`template[${index}].due_at`, e);
                                }}
                                defaultValue={
                                  item.due_at ? moment(item.due_at) : null
                                }
                                style={{
                                  width: "100%",
                                  marginBottom: "0.75rem",
                                }}
                                format="DD/MM/YYYY"
                              />
                            )}
                          />
                        </Col>

                        <Col xs={1} align="middle">
                          <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                            size="medium"
                            onClick={() => remove(index)}
                            style={{ marginBottom: "0.75rem" }}
                          />
                        </Col>
                      </Row>
                    </ItemContainer>
                  );
                })}
              </>
            )}
            <ButtonContainer>
              <Button
                type="outlined"
                icon={<PlusOutlined />}
                onClick={() => {
                  append({
                    title: null,
                    due_at: null,
                  });
                }}
              >
                Add Dateline
              </Button>

              <Button
                htmlType="submit"
                type="primary"
                loading={notificationState.loading.createDateline}
              >
                Create dateline
              </Button>
            </ButtonContainer>
          </form>
        )}
      </>
    </>
  );
};

const ItemContainer = styled.div`
  margin-bottom: ${(props) => props.theme.spacing[4]};
  border-bottom: 1px solid ${(props) => props.theme.colors.grey[200]};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export default ManageDatelineForm;
